import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import moment from "moment";
import Tooltip from "@mui/material/Tooltip";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { DeleteIcon, DownloadIcon, EditIcon, EyeIcon, SearchesCIcon } from "../../../assets/Icons";
import PortalV2Select from "../../common/PortalV2Select";
import { ExpandQR } from "../../modals/ExpandQR";
import DeleteMachine from "../../modals/DeleteMachine";
import NoDataFound from "../../NoDataFound";
import { getInitials } from "../../../utils/util";

const PAGE_SIZE = 10;
const AVATAR_PALETTE = [
  { bg: "#fde8e9", color: "#ca373c" },
  { bg: "#dbeafe", color: "#2563eb" },
  { bg: "#ede9fe", color: "#7c3aed" },
  { bg: "#dcfce7", color: "#16a34a" },
  { bg: "#fef3c7", color: "#d97706" },
];

const getAvatarStyle = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const QrThumb = ({ src, label, fileName, onView }) => (
  <div className="sites-v2__qr">
    {src ? <img src={src} alt={label} /> : "—"}
    {src ? (
      <div className="sites-v2__qr-overlay">
        <Tooltip title="View">
          <button type="button" className="sites-v2__qr-btn" onClick={onView}>
            <EyeIcon />
          </button>
        </Tooltip>
        <Tooltip title="Download">
          <button
            type="button"
            className="sites-v2__qr-btn"
            onClick={() => {
              const link = document.createElement("a");
              link.href = src;
              link.download = fileName;
              link.click();
            }}
          >
            <DownloadIcon />
          </button>
        </Tooltip>
      </div>
    ) : null}
  </div>
);

const FreezerFrostList = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeItems, setActiveItems] = useState({});
  const [timeoutDraft, setTimeoutDraft] = useState({});
  const [savingTimeoutId, setSavingTimeoutId] = useState("");
  const [showQR, setShowQR] = useState({ machineName: null, qr: null, active: false });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const canAdd = userData?.user?.permissions?.includes("MACHINE.ADD");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/freezer-frost/list/${groupId}`, {
        headers: { Authorization: `Bearer ${userData.token}` },
      });
      setList(data.data || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [configData?.config?.API_URL, groupId, userData?.token]);

  useEffect(() => {
    if (groupId && configData?.config?.API_URL && userData?.token) load();
  }, [groupId, configData?.config?.API_URL, userData?.token, load]);

  useEffect(() => {
    setActiveItems(list.reduce((acc, item) => ({ ...acc, [item.uuid]: item.isActive }), {}));
    setTimeoutDraft(
      list.reduce((acc, item) => ({ ...acc, [item.uuid]: Number(item.data?.exitTimeoutHours) || 2 }), {}),
    );
  }, [list]);

  const allTags = useMemo(() => {
    const map = new Map();
    list.forEach((item) => item.tags?.forEach((tag) => tag?.id && map.set(tag.id, tag.name)));
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [list]);

  const stats = useMemo(() => {
    const total = list.length;
    const active = list.filter((item) => item.isActive).length;
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent = list.filter((item) => new Date(item.updatedAt).getTime() >= weekAgo).length;
    return { total, active, hidden: total - active, recent };
  }, [list]);

  const filtered = useMemo(
    () =>
      list.filter((item) => {
        const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
        const matchesVisibility =
          visibilityFilter === "all" ||
          (visibilityFilter === "active" && item.isActive) ||
          (visibilityFilter === "hidden" && !item.isActive);
        const matchesTag = tagFilter === "all" || item.tags?.some((tag) => String(tag.id) === String(tagFilter));
        return matchesSearch && matchesVisibility && matchesTag;
      }),
    [list, search, visibilityFilter, tagFilter],
  );

  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const saveTimeout = async (item) => {
    const hours = Number(timeoutDraft[item.uuid]) || 2;
    setSavingTimeoutId(item.uuid);
    try {
      await axios.put(
        `${configData.config.API_URL}/api/freezer-frost/update/${item.uuid}`,
        {
          name: item.name,
          machineId: item.data?.machineId || "",
          serialNo: item.data?.serialNo || "",
          exitTimeoutHours: hours,
        },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      );
      load();
    } catch (error) {
      console.log(error);
    } finally {
      setSavingTimeoutId("");
    }
  };

  const toggleActive = async (uuid) => {
    const next = !activeItems[uuid];
    setActiveItems((prev) => ({ ...prev, [uuid]: next }));
    try {
      await axios.put(
        `${configData.config.API_URL}/api/machine/update/${uuid}`,
        { isActive: next },
        { headers: { Authorization: `Bearer ${userData.token}` } },
      );
      load();
    } catch (error) {
      console.log(error);
    }
  };

  const deleteHandler = async () => {
    try {
      if (deleteId) {
        await axios.put(
          `${configData.config.API_URL}/api/machine/delete/${deleteId}`,
          {},
          { headers: { Authorization: `Bearer ${userData.token}` } },
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setShowDeleteModal(false);
      load();
    }
  };

  if (loading) {
    return (
      <div className="sites-v2">
        <Skeleton height={36} width={240} />
        <div className="sites-v2__stats mt-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={110} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sites-v2">
      <div className="sites-v2__head">
        <h1 className="sites-v2__title">Freezer-Frost</h1>
        <p className="sites-v2__subtitle">Manage and monitor all configured freezer units</p>
      </div>

      <div className="sites-v2__stats">
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--red">
            <ApartmentOutlinedIcon />
          </div>
          <div>
            <strong>{stats.total}</strong>
            <span>Total Freezers</span>
            <small>All configured freezers</small>
          </div>
        </article>
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--green">
            <CheckCircleOutlineIcon />
          </div>
          <div>
            <strong>{stats.active}</strong>
            <span>Active Freezers</span>
            <small>Visible to users</small>
          </div>
        </article>
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--purple">
            <VisibilityOffOutlinedIcon />
          </div>
          <div>
            <strong>{stats.hidden}</strong>
            <span>Hidden Freezers</span>
            <small>Not visible to users</small>
          </div>
        </article>
        <article className="sites-v2__stat-card">
          <div className="sites-v2__stat-icon sites-v2__stat-icon--amber">
            <ScheduleOutlinedIcon />
          </div>
          <div>
            <strong>{stats.recent}</strong>
            <span>Recently Updated</span>
            <small>In the last 7 days</small>
          </div>
        </article>
      </div>

      {list.length === 0 ? (
        <div className="sites-v2__empty">
          <p>No Freezer-Frost found</p>
          {canAdd && (
            <button type="button" className="sites-v2__add-btn" onClick={() => navigate("/ff/create")}>
              + Add Freezer-Frost
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="sites-v2__toolbar">
            <label className="sites-v2__search">
              <SearchesCIcon />
              <input
                type="text"
                placeholder="Search freezers..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </label>
            <PortalV2Select
              label="Visibility"
              value={visibilityFilter}
              options={[
                { value: "all", label: "All" },
                { value: "active", label: "Active" },
                { value: "hidden", label: "Hidden" },
              ]}
              onChange={(value) => {
                setVisibilityFilter(value);
                setPage(1);
              }}
            />
            <PortalV2Select
              label="Tags"
              value={tagFilter}
              options={[{ value: "all", label: "All tags" }, ...allTags.map((tag) => ({ value: String(tag.id), label: tag.name }))]}
              onChange={(value) => {
                setTagFilter(value);
                setPage(1);
              }}
            />
            <button
              type="button"
              className="sites-v2__reset-btn"
              onClick={() => {
                setSearch("");
                setVisibilityFilter("all");
                setTagFilter("all");
                setPage(1);
              }}
            >
              ↻ Reset
            </button>
            <button type="button" className="sites-v2__reset-btn" onClick={() => navigate(`/ff/dashboard/${groupId}`)}>
              Dashboard
            </button>
            {canAdd && (
              <button type="button" className="sites-v2__add-btn" onClick={() => navigate("/ff/create")}>
                + Add Freezer-Frost
              </button>
            )}
          </div>

          <div className="sites-v2__table-wrap">
            <div className="table-responsive sites-v2__table-scroll">
              <table className="table sites-v2__table mb-0">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Freezer</th>
                    <th>Created At</th>
                    <th>Updated At</th>
                    <th>Tags</th>
                    {canAdd && <th>Visibility</th>}
                    <th>Actions</th>
                    <th>Entry QR</th>
                    <th>Exit QR</th>
                    <th>Exit timeout</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 ? (
                    <tr>
                      <td colSpan={canAdd ? 10 : 9}>
                        <NoDataFound nodatatext="No Results Found" dataInfo="Try adjusting your search or filters" />
                      </td>
                    </tr>
                  ) : (
                    paginated.map((item, index) => {
                      const avatar = getAvatarStyle(item.name);
                      return (
                        <tr key={item.uuid}>
                          <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                          <td>
                            <Link to={`/ff/info/${item.uuid}`} className="sites-v2__site-link">
                              {item.thumbnail ? (
                                <img className="sites-v2__site-thumb" src={item.thumbnail} alt="" />
                              ) : (
                                <span className="sites-v2__site-initials" style={{ backgroundColor: avatar.bg, color: avatar.color }}>
                                  {getInitials(item.name)}
                                </span>
                              )}
                              <span className="sites-v2__site-text">
                                <strong>{item.name}</strong>
                                <small>ID: {item.data?.machineId || "—"}</small>
                              </span>
                            </Link>
                          </td>
                          <td className="sites-v2__date-cell">
                            <div className="sites-v2__date-row">
                              <EventOutlinedIcon className="sites-v2__date-icon" />
                              <span>{moment(item.createdAt).format("MMM D, YYYY")}</span>
                            </div>
                            <small>{moment(item.createdAt).format("h:mm A")}</small>
                          </td>
                          <td className="sites-v2__date-cell">
                            <div className="sites-v2__date-row">
                              <EventOutlinedIcon className="sites-v2__date-icon" />
                              <span>{moment(item.updatedAt).format("MMM D, YYYY")}</span>
                            </div>
                            <small>{moment(item.updatedAt).format("h:mm A")}</small>
                          </td>
                          <td>
                            <div className="sites-v2__tags">
                              {item.tags?.length
                                ? item.tags.map((tag) => (
                                    <span key={tag.id} className="sites-v2__tag">
                                      {tag.name}
                                    </span>
                                  ))
                                : "—"}
                            </div>
                          </td>
                          {canAdd && (
                            <td>
                              <div className="checkbox-apple">
                                <input
                                  className="yep"
                                  id={`ff-check-${item.uuid}`}
                                  type="checkbox"
                                  checked={!!activeItems[item.uuid]}
                                  onChange={() => toggleActive(item.uuid)}
                                />
                                <label htmlFor={`ff-check-${item.uuid}`} />
                              </div>
                            </td>
                          )}
                          <td>
                            <div className="sites-v2__actions">
                              <Tooltip title="Open freezer">
                                <Link to={`/ff/info/${item.uuid}`} className="sites-v2__action-btn">
                                  <EditIcon />
                                </Link>
                              </Tooltip>
                              <Tooltip title={userData?.user?.uuid === item?.owner?.uuid ? "Delete freezer" : "Contact the creator to delete"}>
                                <button
                                  type="button"
                                  className={`sites-v2__action-btn sites-v2__action-btn--danger ${
                                    userData?.user?.uuid === item?.owner?.uuid ? "" : "disabled"
                                  }`}
                                  onClick={() => {
                                    if (userData?.user?.uuid === item?.owner?.uuid) {
                                      setDeleteId(item.uuid);
                                      setShowDeleteModal(true);
                                    }
                                  }}
                                >
                                  <DeleteIcon />
                                </button>
                              </Tooltip>
                              <Tooltip title="More options">
                                <Link to={`/ff/info/${item.uuid}`} className="sites-v2__action-btn">
                                  <MoreVertIcon className="sites-v2__more-icon" />
                                </Link>
                              </Tooltip>
                            </div>
                          </td>
                          <td>
                            <QrThumb
                              src={item.qr}
                              label="Entry QR"
                              fileName={`${item.name}-entry-qr.png`}
                              onView={() => setShowQR({ active: true, qr: item.qr, machineName: `${item.name} Entry` })}
                            />
                          </td>
                          <td>
                            <QrThumb
                              src={item.data?.exitQr}
                              label="Exit QR"
                              fileName={`${item.name}-exit-qr.png`}
                              onView={() => setShowQR({ active: true, qr: item.data?.exitQr, machineName: `${item.name} Exit` })}
                            />
                          </td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <input
                                type="number"
                                min="1"
                                value={timeoutDraft[item.uuid] ?? 2}
                                onChange={(event) =>
                                  setTimeoutDraft((prev) => ({ ...prev, [item.uuid]: event.target.value }))
                                }
                                style={{ width: 64 }}
                              />
                              <span>hrs</span>
                              <button
                                type="button"
                                className="sites-v2__reset-btn"
                                disabled={savingTimeoutId === item.uuid}
                                onClick={() => saveTimeout(item)}
                              >
                                {savingTimeoutId === item.uuid ? "Saving" : "Save"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="sites-v2__pagination">
              <p>
                Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, filtered.length)} of{" "}
                {filtered.length} freezers
              </p>
              <div className="sites-v2__pagination-controls">
                <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  ‹
                </button>
                <span className="active">{page}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  ›
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <ExpandQR showQR={showQR} setShowQR={setShowQR} />
      <DeleteMachine
        showDeleteModal={showDeleteModal}
        onModalCancel={() => {
          setShowDeleteModal(false);
          setDeleteId("");
        }}
        onDeleteClick={deleteHandler}
      />
    </div>
  );
};

export default FreezerFrostList;
